import React from 'react';
import _ from 'underscore';

import { CollectionInlineActionEvent,CollectionInlineActionType, TelemetryEvents } from '~common/telemetryEvents/TelemetryEvents';
import { ContentLocation } from '~common/telemetryEvents/types/ContentTelemetryTypes';
import { generateTelemetryEventHandler } from '~common/telemetryEvents/util/ContentTelemetry';
import { Telemetry } from '~commonInfra/services/Telemetry';
import { ISanitizer } from '~commonInfra/utils/Sanitize';
import { SiteUrlsProps } from '~selectors/SiteUrlsSelectors';
import { DefaultVerticalDividerPadding,ModifiedDateMetadata, OwnerMetadata, VerticalDivider } from '~src/spacesPlatform/components/header/HeaderMetadataRow';
import { ActionsMenuIcon, FavoriteToggleWithCountsIcon, ShareButton } from '~src/spacesPlatform/components/header/HeaderNameRowIcons';
import { ConnectedSpaceHeader } from '~src/spacesPlatform/components/header/SpaceHeader';
import { SpaceHeaderStyles } from '~src/spacesPlatform/components/header/SpaceHeaderStyles';
import { SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';

import { ActionMenuItemClickedChildFunction, ActionMenuItemClickedTelemetryContext } from '../../app/contexts/TelemetryContexts';
import { Messages } from '../../codegen/Localize';
import { RadioEditProvider } from '../../dataRole/utils/RadioEditProvider';
import { IContentInfo } from '../../spacesPlatform/models/IContentInfo';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { ISpaceItemDetailInfo } from '../../ts/models/ISpaceItemDetailInfo';
import { EditableCollectionDescriptionInline } from './CollectionHeaderEditableDescription';
import { EditableCollectionName } from './CollectionHeaderEditableName';
import { CollectionShareTestId } from './CollectionSpace';
import { CollectionsPrivateIndicator } from './CollectionsPrivateIndicator';

export interface CollectionSpaceHeaderProps extends SiteUrlsProps {
  readonly item: ISpaceItemDetailInfo<'collection'>;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
  readonly onShare: () => void;
  readonly isPublicCollectionsEnabled: boolean;  // remove with collectionsPermissionsEnabled feature flag
  readonly selectedContentItemsCount: number;
  readonly isAdminOrOwner: boolean;
  readonly isOwner: boolean;
  readonly sanitizer: ISanitizer;
  readonly reloadSpace: () => void;
}

export function CollectionSpaceHeader(props: CollectionSpaceHeaderProps): JSX.Element {
  const actionsMenuDisabled = props.selectedContentItemsCount > 0;
  const actionsMenuButtonTitle = actionsMenuDisabled ? Messages.collectionActionsMenuDisabled() : null;
  const canEdit = props.isAdminOrOwner;

  const [ editingName, setEditingName ] = React.useState(false);
  const [ editingDescription, setEditingDescription ] = React.useState(false);

  const sendTelemetryForStartEditing = (actionType: CollectionInlineActionType) => {
    Telemetry.sendEvent<CollectionInlineActionEvent>(
      { collection_id: props.item.id, actionType, type: TelemetryEvents.CollectionInlineAction }
    );
  };

  const onNameEdit = (isEditing: boolean) => {
    setEditingName(isEditing);
    if (isEditing) {
      sendTelemetryForStartEditing('rename');
    }
  };
  const onDescriptionEdit = (isEditing: boolean) => {
    setEditingDescription(isEditing);
    if (isEditing) {
      sendTelemetryForStartEditing('set-description');
    }
  };

  const spaceInfo: SpaceInfo = {spaceType: 'collection', item: props.item, subspaceName: undefined};

  return <RadioEditProvider isReadonly={!canEdit}>
    <ConnectedSpaceHeader
      item={props.item}
      availableActions={undefined}
      containerClassName={SpaceHeaderStyles.EditableHeaderStyles}
      nameElement={<EditableCollectionName {...props} canEdit={!editingDescription} onEditChange={onNameEdit}/>}
      hideHeaderIcon={true}
      nameRowIcons={
        <div className={SpaceHeaderStyles.EditableNameRow}>
          {FeatureFlags.useCollectionsFavorites && FavoriteToggleWithCountsIcon({
            item: props.item,
            addToFavorites: props.addToFavorites,
            removeFromFavorites: props.removeFromFavorites,
          })}
          { FeatureFlags.newShareDialogEnabled && FeatureFlags.shareCollectionsEnabled && (props.isPublicCollectionsEnabled || FeatureFlags.collectionsPermissionsEnabled) &&
              <ShareButton onClick={props.onShare} testId={CollectionShareTestId} />
          }
          {
            <ActionMenuItemClickedTelemetryContext.Provider value={generateActionMenuItemClickedTelemetryContext(spaceInfo)}>
              {ActionsMenuIcon(props.item, actionsMenuDisabled, actionsMenuButtonTitle)}
            </ActionMenuItemClickedTelemetryContext.Provider>
          }
        </div>
      }
      descriptionElement={<EditableCollectionDescriptionInline {...props} canEdit={!editingName} onEditChange={onDescriptionEdit}/>}
      metadataRow={
        <>
          {!_.isUndefined(props.item.owner) && OwnerMetadata(props.item.owner, props.urls)}
          {!_.isUndefined(props.item.owner) && VerticalDivider()}
          {ModifiedDateMetadata(props.item.updatedAt, false)}
          {props.item.visibility === 'private' &&
            <>
              {VerticalDivider(DefaultVerticalDividerPadding, 2)}
              <CollectionsPrivateIndicator />
            </>
          }
        </>
      }
    />
  </RadioEditProvider>;
}

export function generateActionMenuItemClickedTelemetryContext(spaceInfo: SpaceInfo) {
  return function ActionMenuItemClickedTelemetryContext(childFunction: ActionMenuItemClickedChildFunction) {
    return (
      childFunction((actionType: string) => {
        const eventHandler = generateTelemetryEventHandler(
          TelemetryEvents.ActionMenuItemClicked,
          spaceInfo,
          [{id: spaceInfo.item.id, contentType: spaceInfo.item.contentType}] as IContentInfo[],
          {id: spaceInfo.item.id, contentType: spaceInfo.item.contentType} as IContentInfo,
          ContentLocation.SpaceHeader,
          null, // content position
          null // ContentTelemetryContextSpecificData
        );
        eventHandler(actionType);
      })
    );
  };
}
